import { useQuery } from "@tanstack/react-query";
import {
  CustomerPackage,
  CustomerPackageCreditLog,
  CustomerPackageDto,
  CustomerPackageExpiryDateLog,
  CustomerPackagePaymentMethodLog,
  CustomerPackageStatusLog,
  QueryToggleOptions,
} from "shared/types";
import client from "shared/utils/axios-config";

// Memberships (unlimited) sort above limited packages in every credit-source
// picker (schedule creation, direct booking, waitlist) — same relative order
// otherwise, so this is stable, not a full re-sort by name/date.
const sortMembershipsFirst = (packages: CustomerPackage[]) =>
  packages.sort(
    (a, b) => Number(b.package_deal.unlimited) - Number(a.package_deal.unlimited),
  );

/* eslint-disable react-hooks/rules-of-hooks */
const getCustomerPackageById = (customerPackageId?: string) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageId],
    queryFn: async (): Promise<CustomerPackageDto | undefined> => {
      const res = await client.get(`/v1/customer_package/${customerPackageId}`);
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: !!customerPackageId,
  });
};

const getAllCustomerPackagesByCustomerId = (customerId?: string) => {
  return useQuery({
    queryKey: ["customer_package", "customer", customerId],
    queryFn: async (): Promise<
      { active: CustomerPackage[]; expired: CustomerPackage[] } | undefined
    > => {
      const res = await client.get(
        `/v1/customer_package/customer/${customerId}`,
      );
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: !!customerId,
  });
};

// occurrence_date (YYYY-MM-DD): anchor the package's validity window on the
// session being booked instead of today — a backdated class is paid for by
// the package that covered that date, never by one bought after it.
const getCustomerPackageByClassCustomerId = (
  customerPackageForm: {
    customer_id?: string;
    class_id: string;
    occurrence_date?: string;
    // "true": also packages the class would cut short (window_conflict).
    include_conflicts?: "true";
  },
  options?: QueryToggleOptions,
) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageForm],
    queryFn: async (): Promise<CustomerPackage[] | undefined> => {
      const res = await client.get(`/v1/customer_package`, {
        params: customerPackageForm,
      });
      if (res.status === 200 && !res.data?.error) {
        return sortMembershipsFirst(res.data.data);
      }
    },
    enabled:
      !!customerPackageForm.customer_id &&
      !!customerPackageForm.class_id &&
      (options?.enabled ?? true),
  });
};

const getCustomerPackageByGroup = (customerPackageForm: {
  customer_ids?: string;
  class_id: string;
  // See getCustomerPackageByClassCustomerId.
  occurrence_date?: string;
}) => {
  return useQuery({
    queryKey: ["customer_package", "group", customerPackageForm],
    queryFn: async (): Promise<
      Record<string, CustomerPackage[]> | undefined
    > => {
      const res = await client.get(`/v1/customer_package/group`, {
        params: customerPackageForm,
      });
      if (res.status === 200 && !res.data?.error) {
        const grouped: Record<string, CustomerPackage[]> = res.data.data;
        Object.values(grouped).forEach(sortMembershipsFirst);
        return grouped;
      }
    },
    enabled: !!customerPackageForm.customer_ids,
  });
};

const getCreditLogsByCustomerPackageId = (customerPackageId?: string) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageId, "credit-log"],
    queryFn: async (): Promise<CustomerPackageCreditLog[] | undefined> => {
      const res = await client.get(
        `/v1/customer_package/${customerPackageId}/credit-log`,
      );
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: !!customerPackageId,
  });
};

const getPaymentMethodLogsByCustomerPackageId = (customerPackageId?: string) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageId, "payment-method-log"],
    queryFn: async (): Promise<CustomerPackagePaymentMethodLog[] | undefined> => {
      const res = await client.get(
        `/v1/customer_package/${customerPackageId}/payment-method-log`,
      );
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: !!customerPackageId,
  });
};

const getExpiryDateLogsByCustomerPackageId = (customerPackageId?: string) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageId, "expiry-date-log"],
    queryFn: async (): Promise<CustomerPackageExpiryDateLog[] | undefined> => {
      const res = await client.get(
        `/v1/customer_package/${customerPackageId}/expiry-date-log`,
      );
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: !!customerPackageId,
  });
};

// enabled defaults to true, but CustomerPackageCard only wants this fetched
// for an already-invalidated, non-preview package — no point requesting it
// for every active package's card.
const getStatusLogsByCustomerPackageId = (customerPackageId?: string, enabled = true) => {
  return useQuery({
    queryKey: ["customer_package", customerPackageId, "status-log"],
    queryFn: async (): Promise<CustomerPackageStatusLog[] | undefined> => {
      const res = await client.get(
        `/v1/customer_package/${customerPackageId}/status-log`,
      );
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    enabled: enabled && !!customerPackageId,
  });
};

export {
  getCustomerPackageById,
  getAllCustomerPackagesByCustomerId,
  getCustomerPackageByClassCustomerId,
  getCustomerPackageByGroup,
  getCreditLogsByCustomerPackageId,
  getPaymentMethodLogsByCustomerPackageId,
  getExpiryDateLogsByCustomerPackageId,
  getStatusLogsByCustomerPackageId,
};
